import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    collection,
    doc,
    getDoc,
    onSnapshot,
} from 'firebase/firestore';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import {
    Avatar,
    ChatContainer,
    InputToolbox,
    MainContainer,
    Message,
    MessageInput,
    MessageList,
} from '@chatscope/chat-ui-kit-react';
import InitialAvatar from 'react-avatar';
import moment from 'moment';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { useFirebase } from 'contexts/FirebaseContext';
import CreatorDMLayout, {
    canReplyToSession,
    toJsDate,
} from 'Layouts/CreatorDMLayout/CreatorDMLayout';
import { replyCreatorDm } from 'services/api/influencer';
import { CreatorDmMessage, CreatorDmSession } from 'types/creator-dm';
import { getErrorMessage, sanitizeURLs } from 'utils/helpers';
import userDummyImg from '../../assets/images/users/user-dummy-img.jpg';
import './creator-dm.scss';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store';
import { toggleSocialAccountView } from 'slices/ui/reducer';
import SocialAccountHandle from 'Components/social-account/SocialAccountHandle';

type UiMessage = {
    id: string;
    text: string;
    direction: 'incoming' | 'outgoing';
    sentTime: string;
    dateKey: string;
    senderLabel: string;
};

const ALLOWED_TAGS = ['b', 'i', 'u', 'strong', 'em', 'br', 'ul', 'ol', 'li', 'p', 'a'];
const ALLOWED_ATTR = ['href', 'rel', 'target'];

const getMessageText = (msg: CreatorDmMessage): string => msg.text ?? '';

const getMessageTimestamp = (msg: CreatorDmMessage) => toJsDate(msg.created_at);

/** `user` = creator (incoming); `model` = agent/ops (outgoing) */
const isOutgoingMessage = (msg: CreatorDmMessage): boolean => msg.role !== 'user';


const getDayLabel = (dateIso: string) => {
    const m = moment(dateIso);
    if (m.isSame(moment(), 'day')) return 'Today';
    if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
    if (m.isAfter(moment().subtract(6, 'days'), 'day')) return m.format('dddd');
    return m.format('DD/MM/YYYY');
};

const CreatorDM = () => {
    useDocumentTitle('Creator DM');

    const { sessionId: activeSessionId } = useParams();
    const { firebaseUser, db } = useFirebase();
    const dispatch: AppDispatch = useDispatch();

    const [activeSession, setActiveSession] = useState<CreatorDmSession | null>(null);
    const [listLoaded, setListLoaded] = useState(false);
    const [rawMessages, setRawMessages] = useState<CreatorDmMessage[]>([]);
    const [messagesLoadedForSession, setMessagesLoadedForSession] = useState<string | null>(null);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const activeSessionIdRef = useRef(activeSessionId);
    activeSessionIdRef.current = activeSessionId;

    const canReply = canReplyToSession(activeSession);

    useEffect(() => {
        if (!activeSessionId) {
            setActiveSession(null);
            setRawMessages([]);
            setMessagesLoadedForSession(null);
            return;
        }
        if (!listLoaded || !firebaseUser || !db) return;
        if (activeSession?.id === activeSessionId) return;

        const fetchSession = async () => {
            try {
                const snap = await getDoc(doc(db, 'creator_dm_sessions', activeSessionId));
                if (snap.exists()) {
                    setActiveSession({ ...(snap.data() as Omit<CreatorDmSession, 'id'>), id: snap.id });
                } else {
                    setActiveSession(null);
                }
            } catch (error) {
                console.error('Failed to fetch creator DM session', error);
            }
        };

        fetchSession();
    }, [listLoaded, firebaseUser, db, activeSessionId, activeSession?.id]);

    // Clear messages only when switching sessions — not on Firebase auth reconnects
    useEffect(() => {
        setRawMessages([]);
        setMessagesLoadedForSession(null);
    }, [activeSessionId]);

    useEffect(() => {
        if (!activeSession?.id || !firebaseUser || !db) {
            return;
        }
        // Wait until route and session state are in sync
        if (activeSession.id !== activeSessionId) {
            return;
        }

        const sessionId = activeSession.id;
        const messagesRef = collection(db, 'creator_dm_sessions', sessionId, 'messages');

        const unsubscribe = onSnapshot(
            messagesRef,
            (snapshot) => {
                // Ignore late snapshots after navigating away / reconnect races
                if (sessionId !== activeSessionIdRef.current) return;

                const messages = snapshot.docs
                    .map((d) => ({
                        id: d.id,
                        ...(d.data() as Omit<CreatorDmMessage, 'id'>),
                    }))
                    .sort((a, b) => {
                        const aTime = getMessageTimestamp(a)?.getTime() ?? 0;
                        const bTime = getMessageTimestamp(b)?.getTime() ?? 0;
                        return aTime - bTime;
                    });
                setRawMessages(messages);
                setMessagesLoadedForSession(sessionId);
            },
            (error) => {
                console.error('Failed to subscribe to creator DM messages', error);
            }
        );

        return () => unsubscribe();
    }, [activeSession?.id, activeSessionId, firebaseUser, db]);


    const uiMessages: UiMessage[] = useMemo(
        () =>
            rawMessages.map((msg) => {
                const date = getMessageTimestamp(msg);
                const direction = isOutgoingMessage(msg) ? 'outgoing' : 'incoming';
                return {
                    id: msg.id,
                    text: getMessageText(msg),
                    direction,
                    sentTime: date ? moment(date).format('h:mma') : '',
                    dateKey: date ? moment(date).startOf('day').toISOString() : 'unknown',
                    senderLabel: direction === 'outgoing' ? (activeSession?.agent || 'Agent') : (
                        activeSession?.social_account?.name ||
                        activeSession?.social_account?.handle ||
                        'Creator'
                    ),
                };
            }),
        [rawMessages, activeSession]
    );

    const groupedMessages = useMemo(() => {
        const groups: Record<string, UiMessage[]> = {};
        uiMessages.forEach((msg) => {
            if (!groups[msg.dateKey]) groups[msg.dateKey] = [];
            groups[msg.dateKey].push(msg);
        });
        return groups;
    }, [uiMessages]);

    const sendReply = async () => {
        const message = DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).trim();
        if (!message || !activeSession || !canReply || sending) return;

        const socialAccountId = activeSession.social_account?.uuid;
        if (!socialAccountId) {
            toast.error('Missing social account for this session');
            return;
        }

        setSending(true);
        try {
            await replyCreatorDm(socialAccountId, activeSession.id, message);
            setInput('');
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setSending(false);
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        setInput((prev) => prev + text);
    };

    const creatorName =
        activeSession?.social_account?.name ||
        activeSession?.social_account?.handle ||
        '';

    return (
        <CreatorDMLayout setActiveSession={setActiveSession} setListLoaded={setListLoaded}>
            <div className="position-relative creator-dm-thread">
                <div className="p-2 border-bottom bg-light chat-ui-header z-5 d-flex align-items-center justify-content-between">
                    <div className="d-flex justify-content-start align-items-center gap-2 overflow-hidden">
                        <div className="flex-shrink-0 d-block d-lg-none">
                            <Link to="/creator-dm" className="user-chat-remove fs-18 p-1 px-2">
                                <i className="ri-arrow-left-s-line align-bottom" />
                            </Link>
                        </div>
                        {activeSession && (
                            <>
                                <button
                                    type="button"
                                    className="btn p-0 border-0 bg-transparent"
                                    onClick={() => {
                                        if (activeSession.social_account?.uuid) {
                                            dispatch(toggleSocialAccountView({
                                                socialAccountId: activeSession.social_account.uuid,
                                            }));
                                        }
                                    }}
                                >
                                    {activeSession.social_account?.profile_picture_url ? (
                                        <Avatar
                                            style={{ height: 40, minHeight: '40px', width: 40, minWidth: '40px' }}
                                            src={activeSession.social_account.profile_picture_url}
                                            name={creatorName}
                                        />
                                    ) : (
                                        <InitialAvatar name={creatorName || 'Creator'} size="40" round />
                                    )}
                                </button>
                                <div className="d-flex flex-column w-100 overflow-hidden">
                                    <h6 className="m-0 text-truncate">{creatorName}</h6>
                                    <span className="text-truncate creator-dm-header-meta d-inline-flex align-items-center gap-1">
                                        {activeSession.social_account?.handle ? (
                                            <SocialAccountHandle
                                                socialAccountId={activeSession.social_account.uuid}
                                                handle={activeSession.social_account.handle}
                                                platform={activeSession.social_account.platform}
                                            />
                                        ) : null}
                                        {activeSession.agent ? (
                                            <span>· Agent: {activeSession.agent}</span>
                                        ) : null}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="chat-container">
                    {activeSession ? (
                        <MainContainer>
                            <ChatContainer>
                                <MessageList className="mb-2">
                                    {Object.keys(groupedMessages).length === 0 ? (
                                        messagesLoadedForSession === activeSession.id ? (
                                            <div className="d-flex h-100 w-100 justify-content-center align-items-center p-4">
                                                <div className="length-zero m-0 text-center">
                                                    <h3>No messages yet</h3>
                                                </div>
                                            </div>
                                        ) : null
                                    ) : (
                                        Object.keys(groupedMessages).map((dateKey) => (
                                            <div key={dateKey}>
                                                <div
                                                    className="position-sticky text-center my-2 d-flex align-items-center justify-content-center"
                                                    style={{ zIndex: 10, top: '1rem' }}
                                                >
                                                    <div className="creator-dm-day-label">
                                                        {dateKey === 'unknown' ? 'Unknown date' : getDayLabel(dateKey)}
                                                    </div>
                                                </div>
                                                {groupedMessages[dateKey].map((msg) => (
                                                    <Message
                                                        key={msg.id}
                                                        model={{
                                                            message: msg.text,
                                                            sentTime: msg.sentTime,
                                                            sender: msg.senderLabel,
                                                            direction: msg.direction,
                                                            position: 'single',
                                                        }}
                                                    >
                                                        {msg.direction === 'incoming' && (
                                                            <Avatar
                                                                name={msg.senderLabel}
                                                                style={{
                                                                    height: 30,
                                                                    width: 30,
                                                                    minHeight: '30px',
                                                                    minWidth: '30px',
                                                                }}
                                                                src={
                                                                    activeSession.social_account?.profile_picture_url ||
                                                                    userDummyImg
                                                                }
                                                            />
                                                        )}
                                                        <Message.CustomContent>
                                                            <Message.HtmlContent
                                                                className={`message-html-content ${msg.direction}`}
                                                                html={DOMPurify.sanitize(
                                                                    sanitizeURLs(msg.text || ''),
                                                                    {
                                                                        ALLOWED_TAGS,
                                                                        ALLOWED_ATTR,
                                                                    }
                                                                )}
                                                            />
                                                            <span className={`msg-sent-time ${msg.direction}`}>
                                                                {msg.sentTime}
                                                            </span>
                                                        </Message.CustomContent>
                                                    </Message>
                                                ))}
                                            </div>
                                        ))
                                    )}
                                </MessageList>
                                {canReply ? (
                                    <MessageInput
                                        placeholder={sending ? 'Sending…' : 'Type your reply…'}
                                        value={input}
                                        onChange={(val) => setInput(val)}
                                        onSend={sendReply}
                                        attachButton={false}
                                        sendDisabled={sending || !input.trim()}
                                        disabled={sending}
                                        onPaste={handlePaste}
                                    />
                                ) : (
                                    <InputToolbox className="creator-dm-reply-disabled">
                                        {!activeSession.last_message_id
                                            ? 'Reply unavailable - waiting for the first creator message.'
                                            : 'Reply window closed - last message is older than 24 hours.'}
                                    </InputToolbox>
                                )}
                            </ChatContainer>
                        </MainContainer>
                    ) : (
                        <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                            <div className="length-zero m-0">
                                <h3>No Chat Selected!</h3>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </CreatorDMLayout>
    );
};

export default CreatorDM;
